import type { ModuleGridType, StudioSystemType } from 'types/studio/items'

import { getVanDerValkModuleGridsForBom } from './vanDerValkModuleGrids'

export type VanDerValkBuildingOutlinePoint = {
  x: number
  y: number
}

export type VanDerValkBuildBuildingOutlineErrorReason =
  | 'no_roof_outline'
  | 'multiple_roof_outlines'
  | 'non_spatial_roof'
  | 'invalid_roof_outline'
  | 'outline_too_complex'

export type BuildVanDerValkBuildingOutlineFromSystemResult =
  | { ok: true; buildingOutline: VanDerValkBuildingOutlinePoint[] }
  | { ok: false; reason: VanDerValkBuildBuildingOutlineErrorReason }

const MAX_OUTLINE_VERTICES = 500
const MM_PER_METRE = 1000
const MIN_AREA_MM2 = 1

/** Runtime OsFacet surface used for roof perimeter extraction. */
type VanDerValkFacetForOutline = {
  uuid?: string
  vertices?: unknown[]
  isNonSpatial: () => boolean
  shapesWithSetbacksJSTS: (useSetbacks: boolean) => {
    facetShape?: {
      getExteriorRing?: () => {
        getCoordinates?: () => Array<{ x: number; y: number }>
      }
      isValid?: () => boolean
    } | null
    facetClippedShape?: unknown
  }
}

type ScenePointM = { x: number; y: number }

function facetFromGrid(grid: ModuleGridType): VanDerValkFacetForOutline | null {
  const facet = grid.facet as VanDerValkFacetForOutline | null | undefined
  if (!facet || typeof facet.isNonSpatial !== 'function') {
    return null
  }
  return facet
}

function collectUniqueFacets(grids: ModuleGridType[]): VanDerValkFacetForOutline[] {
  const byUuid = new Map<string, VanDerValkFacetForOutline>()
  for (const grid of grids) {
    const facet = facetFromGrid(grid)
    if (!facet) {
      continue
    }
    const key = facet.uuid != null ? String(facet.uuid) : `__anon_${byUuid.size}`
    if (!byUuid.has(key)) {
      byUuid.set(key, facet)
    }
  }
  return [...byUuid.values()]
}

function metresToMm(value: number): number {
  return Math.round(value * MM_PER_METRE)
}

function ringPointsMetresFromFacet(facet: VanDerValkFacetForOutline): ScenePointM[] | null {
  if (typeof facet.shapesWithSetbacksJSTS !== 'function') {
    return null
  }
  const shapes = facet.shapesWithSetbacksJSTS(false)
  const shape = shapes?.facetShape
  if (!shape?.getExteriorRing) {
    return null
  }
  const ring = shape.getExteriorRing()
  const coords = ring.getCoordinates?.()
  if (!coords?.length) {
    return null
  }
  const points: ScenePointM[] = []
  for (const c of coords) {
    if (!Number.isFinite(c.x) || !Number.isFinite(c.y)) {
      return null
    }
    points.push({ x: c.x, y: c.y })
  }
  return points
}

function dropClosingDuplicate(points: ScenePointM[]): ScenePointM[] {
  if (points.length < 2) {
    return points
  }
  const first = points[0]
  const last = points[points.length - 1]
  if (first.x === last.x && first.y === last.y) {
    return points.slice(0, -1)
  }
  return points
}

/** Signed area in scene XY (m²); positive = counter-clockwise. */
function signedAreaMetresSq(points: ScenePointM[]): number {
  if (points.length < 3) {
    return 0
  }
  let sum = 0
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length
    sum += points[i].x * points[j].y - points[j].x * points[i].y
  }
  return sum / 2
}

function ensureClockwiseRing(points: ScenePointM[]): ScenePointM[] {
  if (signedAreaMetresSq(points) > 0) {
    return [...points].reverse()
  }
  return points
}

function toOutlineMm(points: ScenePointM[]): VanDerValkBuildingOutlinePoint[] {
  return points.map((p) => ({
    x: metresToMm(p.x),
    y: metresToMm(p.y),
  }))
}

function hasDistinctVerticesMm(outline: VanDerValkBuildingOutlinePoint[]): boolean {
  const seen = new Set<string>()
  for (const p of outline) {
    const key = `${p.x},${p.y}`
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
  }
  return seen.size >= 3
}

function signedAreaMmSq(outline: VanDerValkBuildingOutlinePoint[]): number {
  if (outline.length < 3) {
    return 0
  }
  let sum = 0
  for (let i = 0; i < outline.length; i++) {
    const j = (i + 1) % outline.length
    sum += outline[i].x * outline[j].y - outline[j].x * outline[i].y
  }
  return sum / 2
}

function isValidFacetShape(facet: VanDerValkFacetForOutline): boolean {
  const shape = facet.shapesWithSetbacksJSTS(false)?.facetShape
  if (shape && typeof shape.isValid === 'function') {
    try {
      return shape.isValid()
    } catch {
      return false
    }
  }
  return true
}

export function buildVanDerValkBuildingOutlineFromFacet(
  facet: VanDerValkFacetForOutline
): BuildVanDerValkBuildingOutlineFromSystemResult {
  if (facet.isNonSpatial()) {
    return { ok: false, reason: 'non_spatial_roof' }
  }
  const vertexCount = Array.isArray(facet.vertices) ? facet.vertices.length : 0
  if (vertexCount < 3) {
    return { ok: false, reason: 'invalid_roof_outline' }
  }
  if (!isValidFacetShape(facet)) {
    return { ok: false, reason: 'invalid_roof_outline' }
  }

  let ringM = ringPointsMetresFromFacet(facet)
  if (!ringM || ringM.length < 3) {
    return { ok: false, reason: 'invalid_roof_outline' }
  }
  ringM = dropClosingDuplicate(ringM)
  if (ringM.length < 3) {
    return { ok: false, reason: 'invalid_roof_outline' }
  }
  if (ringM.length > MAX_OUTLINE_VERTICES) {
    return { ok: false, reason: 'outline_too_complex' }
  }

  ringM = ensureClockwiseRing(ringM)
  const buildingOutline = toOutlineMm(ringM)

  if (!hasDistinctVerticesMm(buildingOutline)) {
    return { ok: false, reason: 'invalid_roof_outline' }
  }
  if (Math.abs(signedAreaMmSq(buildingOutline)) < MIN_AREA_MM2) {
    return { ok: false, reason: 'invalid_roof_outline' }
  }

  return { ok: true, buildingOutline }
}

export function buildVanDerValkBuildingOutlineFromSystem(
  system: StudioSystemType | null | undefined
): BuildVanDerValkBuildingOutlineFromSystemResult {
  const grids = getVanDerValkModuleGridsForBom(system)
  if (!grids.length) {
    return { ok: false, reason: 'no_roof_outline' }
  }

  const facets = collectUniqueFacets(grids)
  if (facets.length === 0) {
    return { ok: false, reason: 'non_spatial_roof' }
  }
  if (facets.length > 1) {
    return { ok: false, reason: 'multiple_roof_outlines' }
  }

  return buildVanDerValkBuildingOutlineFromFacet(facets[0])
}

export function vanDerValkBuildingOutlineErrorMessage(reason: VanDerValkBuildBuildingOutlineErrorReason): string {
  const translate = window.translate?.bind(window)
  switch (reason) {
    case 'no_roof_outline':
      return (
        translate?.('mount_vdv_no_roof_outline') ??
        'Draw a roof and place panels on it before generating the Van der Valk BOM.'
      )
    case 'multiple_roof_outlines':
      return (
        translate?.('mount_vdv_multiple_roof_outlines') ??
        'Van der Valk BOM requires all active panel groups on the same roof section.'
      )
    case 'non_spatial_roof':
      return (
        translate?.('mount_vdv_non_spatial_roof') ??
        'Van der Valk requires panels to be placed on a manually drawn roof facet. Draw the roof using Tools (top right), then slightly drag the panels onto the new roof.'
      )
    case 'outline_too_complex':
      return (
        translate?.('mount_vdv_outline_too_complex') ?? 'Roof outline has too many vertices for the Van der Valk API.'
      )
    case 'invalid_roof_outline':
    default:
      return (
        translate?.('mount_vdv_invalid_roof_outline') ??
        'Roof outline is invalid. Check the drawn roof perimeter and try again.'
      )
  }
}
